/**
 * Firestore data access for prompts and prompt interactions.
 *
 * Collections:
 *   prompts/{promptId}                    org-scoped prompts (orgId = email domain)
 *   globalPrompts/{promptId}              admin-managed, visible to every org
 *   globalPrompts/{id}/orgStats/{orgId}   per-org vote/copy counters for a global prompt
 *   duplicatePrompts/{duplicateId}        per-user editable copies of prompts
 *   users/{uid}/votes/{promptId}          the user's vote ('upvote'/'downvote')
 *   users/{uid}/favourites/{promptId}     favourite marker
 *   users/{uid}/hiddenPrompts/{promptId}  "deleted" own prompts (hidden, not removed)
 *
 * Counters (upvotes/downvotes/favoriteCount/copyCount) are denormalized onto
 * the prompt (or orgStats) doc and updated in the same transaction as the
 * per-user marker doc, so they stay consistent without a server.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  runTransaction,
  increment,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { auth, db, orgIdFromEmail } from '../firebase';

const now = () => new Date().toISOString();

function requireUser() {
  const fbUser = auth.currentUser;
  if (!fbUser) throw new Error('Not signed in');
  return { uid: fbUser.uid, orgId: orgIdFromEmail(fbUser.email) };
}

/** Shape a Firestore prompt doc like the old REST API responses. */
function mapPrompt(data, { isGlobal = false, orgStats = null, voteType = null, isFavorite = false } = {}) {
  if (!data) return null;
  const categories = Array.isArray(data.categories) ? data.categories : [];
  return {
    id: data.promptId,
    title: data.title || '',
    description: data.description || '',
    promptText: data.promptText || '',
    category: categories[0] || '',
    categories,
    creatorId: data.creatorId || null,
    creatorName: data.creatorName || '',
    creatorEmail: data.creatorEmail || '',
    organizationId: isGlobal ? null : data.orgId || null,
    backgroundTheory: data.backgroundTheory || null,
    userId: data.userId || null,
    originalPromptId: data.originalPromptId || null,
    upvotes: isGlobal ? orgStats?.upvotes || 0 : data.upvotes || 0,
    downvotes: isGlobal ? orgStats?.downvotes || 0 : data.downvotes || 0,
    favoriteCount: data.favoriteCount || 0,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    hasUpvoted: voteType === 'upvote',
    hasDownvoted: voteType === 'downvote',
    isFavorite,
    unpaidCopilot: data.unpaidCopilot === true,
    isGlobal,
    promptFormat: data.promptFormat || null,
  };
}

/** Load the user's per-prompt markers in three cheap queries. */
async function fetchUserMarkers(uid) {
  const [votesSnap, favsSnap, hiddenSnap] = await Promise.all([
    getDocs(collection(db, 'users', uid, 'votes')),
    getDocs(collection(db, 'users', uid, 'favourites')),
    getDocs(collection(db, 'users', uid, 'hiddenPrompts')),
  ]);
  const votes = {};
  votesSnap.forEach((d) => {
    votes[d.id] = d.data().voteType;
  });
  const favourites = new Set();
  favsSnap.forEach((d) => favourites.add(d.id));
  const hidden = new Set();
  hiddenSnap.forEach((d) => hidden.add(d.id));
  return { votes, favourites, hidden };
}

async function fetchOrgStats(promptIds, orgId) {
  const snaps = await Promise.all(
    promptIds.map((id) => getDoc(doc(db, 'globalPrompts', id, 'orgStats', orgId)))
  );
  const stats = {};
  snaps.forEach((snap, i) => {
    stats[promptIds[i]] = snap.exists() ? snap.data() : null;
  });
  return stats;
}

/**
 * Fetch org + global prompts with per-user interaction flags.
 * Filters (category, search, activityTypeId) are applied client-side —
 * libraries are small enough that this beats maintaining composite indexes.
 */
export async function fetchPrompts({ promptType = 'all', category, search, activityTypeId, adminView = false } = {}) {
  const { uid, orgId } = requireUser();

  const wantOrg = promptType === 'all' || promptType === 'org';
  const wantGlobal = promptType === 'all' || promptType === 'global';

  // adminView reads the whole prompts collection (cross-org); Firestore
  // rules only permit that for admin-domain users.
  const orgQuery = adminView
    ? collection(db, 'prompts')
    : query(collection(db, 'prompts'), where('orgId', '==', orgId));

  const [orgSnap, globalSnap, markers] = await Promise.all([
    wantOrg ? getDocs(orgQuery) : Promise.resolve(null),
    wantGlobal ? getDocs(collection(db, 'globalPrompts')) : Promise.resolve(null),
    fetchUserMarkers(uid),
  ]);

  const globalDocs = [];
  if (globalSnap) globalSnap.forEach((d) => globalDocs.push(d.data()));
  const orgStats = globalDocs.length
    ? await fetchOrgStats(globalDocs.map((d) => d.promptId), orgId)
    : {};

  let prompts = [];
  if (orgSnap) {
    orgSnap.forEach((d) => {
      const data = d.data();
      prompts.push(
        mapPrompt(data, {
          voteType: markers.votes[data.promptId] || null,
          isFavorite: markers.favourites.has(data.promptId),
        })
      );
    });
  }
  globalDocs.forEach((data) => {
    prompts.push(
      mapPrompt(data, {
        isGlobal: true,
        orgStats: orgStats[data.promptId],
        voteType: markers.votes[data.promptId] || null,
        isFavorite: markers.favourites.has(data.promptId),
      })
    );
  });

  if (category) {
    prompts = prompts.filter((p) => p.categories.includes(category));
  }
  if (search) {
    const term = search.toLowerCase();
    prompts = prompts.filter(
      (p) =>
        p.title.toLowerCase().includes(term) ||
        p.description.toLowerCase().includes(term) ||
        p.promptText.toLowerCase().includes(term)
    );
  }
  if (activityTypeId) {
    const atSnap = await getDoc(doc(db, 'activityTypes', activityTypeId));
    const linked = new Set(atSnap.exists() ? atSnap.data().promptIds || [] : []);
    prompts = prompts.filter((p) => linked.has(p.id));
  }

  return prompts;
}

/** Resolve a prompt by ID from either collection, with user flags. */
export async function fetchPromptById(promptId) {
  const { uid, orgId } = requireUser();

  const [orgDoc, globalDoc, voteDoc, favDoc] = await Promise.all([
    getDoc(doc(db, 'prompts', promptId)),
    getDoc(doc(db, 'globalPrompts', promptId)),
    getDoc(doc(db, 'users', uid, 'votes', promptId)),
    getDoc(doc(db, 'users', uid, 'favourites', promptId)),
  ]);

  const voteType = voteDoc.exists() ? voteDoc.data().voteType : null;
  const isFavorite = favDoc.exists();

  if (orgDoc.exists()) {
    return mapPrompt(orgDoc.data(), { voteType, isFavorite });
  }
  if (globalDoc.exists()) {
    const stats = await getDoc(doc(db, 'globalPrompts', promptId, 'orgStats', orgId));
    return mapPrompt(globalDoc.data(), {
      isGlobal: true,
      orgStats: stats.exists() ? stats.data() : null,
      voteType,
      isFavorite,
    });
  }
  return null;
}

/**
 * Toggle a vote (same type removes it, different type switches it) and keep
 * the denormalized counters in step, all in one transaction.
 * Returns { voteType, prompt } like the old POST /api/votes.
 */
export async function toggleVote(promptId, requestedType) {
  const { uid, orgId } = requireUser();

  const voteRef = doc(db, 'users', uid, 'votes', promptId);
  const orgPromptRef = doc(db, 'prompts', promptId);
  const globalPromptRef = doc(db, 'globalPrompts', promptId);
  const orgStatsRef = doc(db, 'globalPrompts', promptId, 'orgStats', orgId);

  const result = await runTransaction(db, async (tx) => {
    const [voteSnap, orgSnap, globalSnap, statsSnap] = await Promise.all([
      tx.get(voteRef),
      tx.get(orgPromptRef),
      tx.get(globalPromptRef),
      tx.get(orgStatsRef),
    ]);

    const isGlobal = !orgSnap.exists() && globalSnap.exists();
    if (!orgSnap.exists() && !globalSnap.exists()) {
      throw new Error('Prompt not found');
    }

    const previousType = voteSnap.exists() ? voteSnap.data().voteType : null;
    const nextType = previousType === requestedType ? null : requestedType;

    const delta = { upvotes: 0, downvotes: 0 };
    if (previousType === 'upvote') delta.upvotes -= 1;
    if (previousType === 'downvote') delta.downvotes -= 1;
    if (nextType === 'upvote') delta.upvotes += 1;
    if (nextType === 'downvote') delta.downvotes += 1;

    if (nextType) {
      tx.set(voteRef, { promptId, voteType: nextType, isGlobal, orgId, updatedAt: now() });
    } else {
      tx.delete(voteRef);
    }

    if (isGlobal) {
      const current = statsSnap.exists() ? statsSnap.data() : { upvotes: 0, downvotes: 0, copyCount: 0 };
      tx.set(
        orgStatsRef,
        {
          orgId,
          upvotes: Math.max(0, (current.upvotes || 0) + delta.upvotes),
          downvotes: Math.max(0, (current.downvotes || 0) + delta.downvotes),
          copyCount: current.copyCount || 0,
          updatedAt: now(),
        }
      );
      return { nextType, isGlobal, data: globalSnap.data(), stats: {
        upvotes: Math.max(0, (current.upvotes || 0) + delta.upvotes),
        downvotes: Math.max(0, (current.downvotes || 0) + delta.downvotes),
      } };
    }

    if (delta.upvotes || delta.downvotes) {
      tx.update(orgPromptRef, {
        upvotes: increment(delta.upvotes),
        downvotes: increment(delta.downvotes),
        updatedAt: now(),
      });
    }
    const data = orgSnap.data();
    return {
      nextType,
      isGlobal,
      data: {
        ...data,
        upvotes: Math.max(0, (data.upvotes || 0) + delta.upvotes),
        downvotes: Math.max(0, (data.downvotes || 0) + delta.downvotes),
      },
    };
  });

  const [favDoc] = await Promise.all([getDoc(doc(db, 'users', uid, 'favourites', promptId))]);
  return {
    voteType: result.nextType,
    prompt: mapPrompt(result.data, {
      isGlobal: result.isGlobal,
      orgStats: result.stats || null,
      voteType: result.nextType,
      isFavorite: favDoc.exists(),
    }),
  };
}

/** Toggle a favourite and its counter. Returns { isFavorite, prompt }. */
export async function toggleFavorite(promptId) {
  const { uid } = requireUser();

  const favRef = doc(db, 'users', uid, 'favourites', promptId);
  const orgPromptRef = doc(db, 'prompts', promptId);
  const globalPromptRef = doc(db, 'globalPrompts', promptId);

  const result = await runTransaction(db, async (tx) => {
    const [favSnap, orgSnap, globalSnap] = await Promise.all([
      tx.get(favRef),
      tx.get(orgPromptRef),
      tx.get(globalPromptRef),
    ]);

    const isGlobal = !orgSnap.exists() && globalSnap.exists();
    if (!orgSnap.exists() && !globalSnap.exists()) {
      throw new Error('Prompt not found');
    }
    const promptRef = isGlobal ? globalPromptRef : orgPromptRef;
    const data = isGlobal ? globalSnap.data() : orgSnap.data();

    const nextIsFavorite = !favSnap.exists();
    if (nextIsFavorite) {
      tx.set(favRef, { promptId, isGlobal, createdAt: now() });
    } else {
      tx.delete(favRef);
    }
    tx.update(promptRef, { favoriteCount: increment(nextIsFavorite ? 1 : -1) });

    return {
      isGlobal,
      nextIsFavorite,
      data: {
        ...data,
        favoriteCount: Math.max(0, (data.favoriteCount || 0) + (nextIsFavorite ? 1 : -1)),
      },
    };
  });

  const voteDoc = await getDoc(doc(db, 'users', uid, 'votes', promptId));
  return {
    isFavorite: result.nextIsFavorite,
    prompt: mapPrompt(result.data, {
      isGlobal: result.isGlobal,
      voteType: voteDoc.exists() ? voteDoc.data().voteType : null,
      isFavorite: result.nextIsFavorite,
    }),
  };
}

/** The user's favourite prompts, resolved to full prompt objects. */
export async function fetchFavoritePrompts() {
  const { uid, orgId } = requireUser();
  const favsSnap = await getDocs(collection(db, 'users', uid, 'favourites'));
  const entries = [];
  favsSnap.forEach((d) => entries.push(d.data()));

  const results = await Promise.all(
    entries.map(async (entry) => {
      const ref = entry.isGlobal
        ? doc(db, 'globalPrompts', entry.promptId)
        : doc(db, 'prompts', entry.promptId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      let orgStats = null;
      if (entry.isGlobal) {
        const statsSnap = await getDoc(doc(db, 'globalPrompts', entry.promptId, 'orgStats', orgId));
        orgStats = statsSnap.exists() ? statsSnap.data() : null;
      }
      const voteDoc = await getDoc(doc(db, 'users', uid, 'votes', entry.promptId));
      return mapPrompt(snap.data(), {
        isGlobal: !!entry.isGlobal,
        orgStats,
        voteType: voteDoc.exists() ? voteDoc.data().voteType : null,
        isFavorite: true,
      });
    })
  );
  return results.filter(Boolean);
}

/** Fire-and-forget copy tracking. */
export async function recordCopy(promptId) {
  const { orgId } = requireUser();
  const orgDoc = await getDoc(doc(db, 'prompts', promptId));
  if (orgDoc.exists()) {
    await updateDoc(doc(db, 'prompts', promptId), { copyCount: increment(1) });
    return;
  }
  const globalDoc = await getDoc(doc(db, 'globalPrompts', promptId));
  if (globalDoc.exists()) {
    const statsRef = doc(db, 'globalPrompts', promptId, 'orgStats', orgId);
    const statsSnap = await getDoc(statsRef);
    const current = statsSnap.exists() ? statsSnap.data() : { upvotes: 0, downvotes: 0, copyCount: 0 };
    await setDoc(statsRef, { ...current, orgId, copyCount: (current.copyCount || 0) + 1, updatedAt: now() });
  }
}

/** Create an org prompt. Accepts the shape the Add Prompt screen submits. */
export async function createPrompt(promptData) {
  const { uid, orgId } = requireUser();
  const fbUser = auth.currentUser;
  const promptId = promptData.promptId || uuidv4();
  const docData = {
    promptId,
    title: promptData.title || '',
    description: promptData.description || promptData.promptDescription || '',
    promptText: promptData.promptText || '',
    categories: promptData.categories || (promptData.category ? [promptData.category] : []),
    creatorId: uid,
    creatorName: promptData.creatorName || fbUser.displayName || '',
    creatorEmail: fbUser.email || '',
    orgId,
    backgroundTheory: promptData.backgroundTheory || null,
    promptFormat: promptData.promptFormat || null,
    unpaidCopilot: promptData.unpaidCopilot === true,
    upvotes: 0,
    downvotes: 0,
    favoriteCount: 0,
    copyCount: 0,
    createdAt: now(),
    updatedAt: now(),
  };
  await setDoc(doc(db, 'prompts', promptId), docData);
  return mapPrompt(docData);
}

/** Create a global prompt (rules restrict to admins). */
export async function createGlobalPrompt(promptData) {
  const { uid } = requireUser();
  const fbUser = auth.currentUser;
  const promptId = promptData.promptId || uuidv4();
  const docData = {
    promptId,
    title: promptData.title || '',
    description: promptData.description || promptData.promptDescription || '',
    promptText: promptData.promptText || '',
    categories: promptData.categories || (promptData.category ? [promptData.category] : []),
    creatorId: uid,
    creatorName: promptData.creatorName || fbUser.displayName || '',
    creatorEmail: fbUser.email || '',
    backgroundTheory: promptData.backgroundTheory || null,
    promptFormat: promptData.promptFormat || null,
    unpaidCopilot: promptData.unpaidCopilot === true,
    favoriteCount: 0,
    createdAt: now(),
    updatedAt: now(),
  };
  await setDoc(doc(db, 'globalPrompts', promptId), docData);
  return mapPrompt(docData, { isGlobal: true });
}

/** Update fields on an org or global prompt. */
export async function updatePromptDoc(promptId, updates) {
  const orgRef = doc(db, 'prompts', promptId);
  const orgSnap = await getDoc(orgRef);
  const ref = orgSnap.exists() ? orgRef : doc(db, 'globalPrompts', promptId);
  const isGlobal = !orgSnap.exists();

  const fields = { updatedAt: now() };
  if (updates.title !== undefined) fields.title = updates.title;
  if (updates.description !== undefined) fields.description = updates.description;
  if (updates.promptText !== undefined) fields.promptText = updates.promptText;
  if (updates.backgroundTheory !== undefined) fields.backgroundTheory = updates.backgroundTheory;
  if (updates.categories !== undefined) fields.categories = updates.categories;
  if (updates.promptFormat !== undefined) fields.promptFormat = updates.promptFormat;

  await updateDoc(ref, fields);
  const snap = await getDoc(ref);
  return mapPrompt(snap.data(), { isGlobal });
}

/** Hard-delete a prompt (creator or admin; rules enforce). */
export async function deletePromptDoc(promptId) {
  const orgSnap = await getDoc(doc(db, 'prompts', promptId));
  if (orgSnap.exists()) {
    await deleteDoc(doc(db, 'prompts', promptId));
    return true;
  }
  const globalSnap = await getDoc(doc(db, 'globalPrompts', promptId));
  if (globalSnap.exists()) {
    await deleteDoc(doc(db, 'globalPrompts', promptId));
    return true;
  }
  return false;
}

/** Hide one of the user's own prompts ("delete" in the UI). */
export async function hidePrompt(promptId) {
  const { uid } = requireUser();
  await setDoc(doc(db, 'users', uid, 'hiddenPrompts', promptId), { promptId, hiddenAt: now() });
  return true;
}

export async function fetchHiddenPromptIds() {
  const { uid } = requireUser();
  const snap = await getDocs(collection(db, 'users', uid, 'hiddenPrompts'));
  const ids = [];
  snap.forEach((d) => ids.push(d.id));
  return ids;
}

// ---- Duplicate prompts (per-user editable copies) ----------------------

export async function createDuplicate(promptId) {
  const { uid, orgId } = requireUser();
  const source = await fetchPromptById(promptId);
  if (!source) throw new Error('Prompt not found');

  const duplicateId = uuidv4();
  const docData = {
    duplicateId,
    promptId: duplicateId,
    originalPromptId: promptId,
    userId: uid,
    orgId,
    title: source.title,
    description: source.description,
    promptText: source.promptText,
    categories: source.categories,
    backgroundTheory: source.backgroundTheory,
    promptFormat: source.promptFormat,
    createdAt: now(),
    updatedAt: now(),
  };
  await setDoc(doc(db, 'duplicatePrompts', duplicateId), docData);
  return mapPrompt({ ...docData, creatorId: uid });
}

export async function fetchDuplicate(duplicateId) {
  const snap = await getDoc(doc(db, 'duplicatePrompts', duplicateId));
  if (!snap.exists()) throw new Error('Duplicate prompt not found');
  const data = snap.data();
  return mapPrompt({ ...data, creatorId: data.userId });
}

/** The user's duplicate of a given original prompt, if any. */
export async function findUserDuplicate(originalPromptId) {
  const { uid } = requireUser();
  const snap = await getDocs(
    query(
      collection(db, 'duplicatePrompts'),
      where('userId', '==', uid),
      where('originalPromptId', '==', originalPromptId)
    )
  );
  if (snap.empty) return null;
  const data = snap.docs[0].data();
  return mapPrompt({ ...data, creatorId: data.userId });
}

export async function updateDuplicate(duplicateId, updates) {
  const ref = doc(db, 'duplicatePrompts', duplicateId);
  const fields = { updatedAt: now() };
  if (updates.title !== undefined) fields.title = updates.title;
  if (updates.description !== undefined) fields.description = updates.description;
  if (updates.promptText !== undefined) fields.promptText = updates.promptText;
  if (updates.backgroundTheory !== undefined) fields.backgroundTheory = updates.backgroundTheory;
  if (updates.categories !== undefined) fields.categories = updates.categories;
  await updateDoc(ref, fields);
  const snap = await getDoc(ref);
  const data = snap.data();
  return mapPrompt({ ...data, creatorId: data.userId });
}

export async function deleteDuplicate(duplicateId) {
  await deleteDoc(doc(db, 'duplicatePrompts', duplicateId));
  return true;
}
